import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { LoggerService } from 'app/services/logger.service';
import { ApplicationRoute } from 'app/models/application-route';
import { AdminService } from 'app/services/admin.service';
import { UserService } from 'app/services/user.service';

/**
 * Creates a reusable route guard that checks if a user is authenticated.
 * Redirects to the given `redirectTo` UrlTree if not authenticated.
 * This is used to create Fortinet FAC employee guard for admins and
 * user FortiCloud guard to access portal.
 *
 * @param isAuthenticated - Signal (or any getter) returning the authentication state
 * @param redirectTo - UrlTree to redirect to if not authenticated
 * @param logScope - Label used for logging context
 * @returns A CanActivateFn function to be used as a route guard
 */
export function createAuthGuard(
    isAuthenticated: () => boolean,
    redirectTo: UrlTree,
    logScope: string
): CanActivateFn {
    return (): boolean | UrlTree => {
        // Inject scoped logger
        const logger = inject(LoggerService).getScopedLogger(logScope);

        logger.debug('Checking access...');

        const authenticated: boolean = isAuthenticated();
        logger.debug('Authenticated:', authenticated);

        if (authenticated) {
            logger.debug('Access granted.');
            return true;
        }

        // User not authenticated, redirect to splash or fallback route
        logger.warn('Not authenticated. Redirecting...');
        return redirectTo;
    };
}

// Guard to protect admin routes using FAC Fortinet employee auth
export const AdminAuthGuard: CanActivateFn = (route, state) => createAuthGuard(
    inject(AdminService).isAuthenticated,                           // Admin auth state
    inject(Router).createUrlTree([ApplicationRoute.ADMIN_SPLASH]),  // Redirect for unauthenticated admins
    'AdminAuthGuard'                                                // Logger scope
)(route, state);

// Guard to protect regular user routes using FortiCloud auth
export const UserAuthGuard: CanActivateFn = (route, state) => createAuthGuard(
    inject(UserService).isAuthenticated,                       // User auth state
    inject(Router).createUrlTree([ApplicationRoute.SPLASH]),   // Redirect for unauthenticated users
    'UserAuthGuard'                                            // Logger scope
)(route, state);
